import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_NUMBER = "27659029122";

const LOGO_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/3sw9i27n_ChatGPT%20Image%20Jan%2027%2C%20.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? "glass-nav border-b border-spa-neutral-200" : "bg-transparent"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="Kriel Colon & Beauty Spa" 
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-body text-sm tracking-wide transition-colors ${
                  isActive(link.path)
                    ? "text-spa-pink-600 font-semibold"
                    : "text-spa-neutral-600 hover:text-spa-pink-500"
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/booking" data-testid="nav-book-now">
              <Button className="bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg px-6 py-2 font-medium transition-all hover:shadow-lg">
                Book Now
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-spa-neutral-700" />
            ) : (
              <Menu className="h-6 w-6 text-spa-neutral-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-6 border-t border-spa-neutral-200 mt-2 pt-4 bg-white/95">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-body text-base py-2 ${
                    isActive(link.path)
                      ? "text-spa-pink-600 font-semibold"
                      : "text-spa-neutral-600"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/booking" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg py-3 font-medium mt-2">
                  Book Now
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-spa-neutral-100 border-t border-spa-neutral-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="Kriel Colon & Beauty Spa" 
                className="h-16 w-auto object-contain mb-4"
              />
            </Link>
            <p className="text-spa-neutral-500 text-sm leading-relaxed">
              Where beauty meets art. Treat yourself to a luxurious experience with our expert health and beauty services.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-spa-neutral-800 mb-4">
              Quick Links
            </h4>
            <nav className="flex flex-col gap-3">
              {["Services", "Booking", "About", "Contact"].map((item) => (
                <Link key={item} to={`/${item.toLowerCase()}`} className="text-spa-neutral-500 hover:text-spa-pink-500 text-sm transition-colors">
                  {item === "Booking" ? "Book Appointment" : item}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-spa-neutral-800 mb-4">
              Contact Us
            </h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-spa-pink flex-shrink-0 mt-0.5" />
                <span className="text-spa-neutral-500 text-sm">32 Kraanvoel Avenue, Kriel</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-spa-pink flex-shrink-0" />
                <a href="tel:0659029122" className="text-spa-neutral-500 hover:text-spa-pink-500 text-sm transition-colors">
                  065 902 9122
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-spa-pink flex-shrink-0 mt-0.5" />
                <div className="text-spa-neutral-500 text-sm">
                  <p>Mon-Fri: 08:00 - 17:00</p>
                  <p>Sat: 08:00 - 15:00</p>
                  <p>Sun: Closed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-spa-neutral-800 mb-4">
              Legal
            </h4>
            <nav className="flex flex-col gap-3">
              <Link to="/terms" className="text-spa-neutral-500 hover:text-spa-pink-500 text-sm transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-spa-neutral-500 hover:text-spa-pink-500 text-sm transition-colors">
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-spa-neutral-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-spa-neutral-400 text-sm">
              © {new Date().getFullYear()} Kriel Colon and Beauty Spa. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <span className="text-spa-neutral-400 text-sm">Trusted by:</span>
              <span className="text-spa-neutral-600 text-sm font-medium">Biomedical Emporium</span>
              <span className="text-spa-neutral-400">•</span>
              <span className="text-spa-neutral-600 text-sm font-medium">Zena Cosmetics</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const WhatsAppButton = () => {
  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi! I'd like to inquire about your spa services.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <button
      onClick={handleWhatsApp}
      className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BA5C] text-white p-4 rounded-full shadow-lg whatsapp-pulse transition-transform hover:scale-110"
      data-testid="whatsapp-button"
      aria-label="Contact us on WhatsApp"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </button>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow pt-20">
        {children}
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
